"use client";

import { useMemo } from "react";
import { useTranslations } from "next-intl";
import { useQuery } from "@tanstack/react-query";

import { Link } from "@/i18n/navigation";
import { ProfilePreviewTrigger } from "@/features/profile-preview/ui/profile-preview-trigger";
import { TwitchStatusIcon } from "@/features/stream/twitch";
import { api } from "@/shared/lib/api";
import { getOpggUrl } from "@/shared/lib/opgg";
import { withDiscordAvatarSize } from "@/shared/lib/image-utils";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/ui/data-display/avatar";
import { Button } from "@/shared/ui/buttons/button";
import { Card, CardContent } from "@/shared/ui/cards/card";
import { RoleIcon } from "@/shared/ui/data-display/role-icon";
import { RankPill } from "./team-card-bits";
import { useTournamentStreams } from "@/entities/tournament/api/use-tournament-streams";
import { QUERY_VOLATILITY } from "@/shared/config/api-constants";

type SoloParticipant = {
  id: string;
  userId: string;
  userDisplayName: string;
  userAvatarUrl?: string | null;
  riotAccountGameName?: string | null;
  riotAccountTagLine?: string | null;
  /** Регион аккаунта (RU/EUW1/…) — для корректной op.gg-ссылки. */
  riotAccountRegion?: string | null;
  rank?: string | null;
  mainRole?: string | null;
  status?: string | null;
};

async function fetchParticipants(tournamentId: string) {
  const res = await api.get<SoloParticipant[]>(
    `/tournaments/${encodeURIComponent(tournamentId)}/participants`,
  );
  return Array.isArray(res.data) ? res.data : [];
}

/**
 * Сетка участников турнира 1v1 — простые solo-карточки (игрок, а не команда):
 * аватар, ник → профиль, Riot ID → OP.GG, ранг. Без команд/слотов/«состава».
 */
export function TournamentSoloParticipantsGrid({
  tournamentId,
}: {
  tournamentId: string;
}) {
  const tUi = useTranslations("Ui");
  const { data: participants = [], isLoading } = useQuery({
    queryKey: ["tournaments", tournamentId, "participants", "solo"],
    queryFn: () => fetchParticipants(tournamentId),
    enabled: Boolean(tournamentId),
    ...QUERY_VOLATILITY.STANDARD,
  });

  // Twitch-статусы игроков (KAN-93) — один батч-запрос на страницу турнира.
  const { data: streams } = useTournamentStreams(tournamentId);
  const streamByUserId = useMemo(
    () => new Map((streams?.playerStreams ?? []).map((s) => [s.userId, s])),
    [streams],
  );

  if (isLoading) return null;

  if (participants.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        {tUi("tournamentSoloParticipantsGrid.pokaNetUchastnikov")}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {participants.map((p) => {
        const name = p.userDisplayName || "—";
        const hasRiot = p.riotAccountGameName && p.riotAccountTagLine;
        const opggUrl = hasRiot
          ? getOpggUrl(p.riotAccountGameName!, p.riotAccountTagLine!, p.riotAccountRegion ?? undefined)
          : null;

        return (
          <Card
            key={p.id}
            className="relative overflow-hidden rounded-[14px] border border-border bg-card shadow-sm transition-all hover:border-primary/40 hover:shadow-md"
          >
            <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-[radial-gradient(120%_180%_at_15%_0%,color-mix(in_srgb,var(--primary)_16%,transparent),transparent_70%)]" />
            <CardContent className="relative flex flex-col gap-3 p-4">
              <div className="flex items-center gap-3">
                <ProfilePreviewTrigger userId={p.userId}>
                  <Avatar className="h-14 w-14 cursor-pointer border border-border/60">
                    <AvatarImage
                      src={withDiscordAvatarSize(p.userAvatarUrl, 56)}
                      alt={name}
                      loading="lazy"
                      decoding="async"
                    />
                    <AvatarFallback className="text-xl font-bold">
                      {name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </ProfilePreviewTrigger>
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <Link href={`/u/${p.userId}`} className="min-w-0 hover:underline">
                      <span className="block truncate text-lg font-bold leading-none transition-colors hover:text-primary">
                        {name}
                      </span>
                    </Link>
                    <TwitchStatusIcon stream={streamByUserId.get(p.userId)} />
                  </div>
                  {hasRiot ? (
                    <a
                      href={opggUrl ?? "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 block w-full truncate text-left text-xs text-muted-foreground hover:text-primary hover:underline"
                    >
                      {p.riotAccountGameName}#{p.riotAccountTagLine}
                    </a>
                  ) : null}
                  {(p.mainRole || p.rank) && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      {p.mainRole && (
                        <span className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-secondary px-2 py-[3px] text-secondary-foreground">
                          <RoleIcon role={p.mainRole} size={14} />
                          <span className="text-[10px] font-semibold uppercase tracking-[0.06em]">
                            {p.mainRole}
                          </span>
                        </span>
                      )}
                      <RankPill rank={p.rank} />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <Button asChild variant="outline" size="sm" className="flex-1 text-xs font-medium">
                  <Link href={`/u/${p.userId}`}>{tUi("tournamentSoloParticipantsGrid.profil")}</Link>
                </Button>
                {opggUrl ? (
                  <Button asChild variant="secondary" size="sm" className="flex-1 text-xs font-medium">
                    <a href={opggUrl} target="_blank" rel="noopener noreferrer">
                      OP.GG
                    </a>
                  </Button>
                ) : null}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
